import { makeStyles } from '@material-ui/core/styles';

/**
 * BRAND COLORS - Single source of truth for all billing/CRM components
 * Update here to change colors across entire application
 */
export const BRAND_COLORS = {
  navy: '#002D55',
  orange: '#F79500',
  white: '#FFFFFF',
  lightGray: '#f5f5f5',
  lightBlue: '#e6eaf0',
  darkGray: '#666666',
  textSecondary: '#757575',
  success: '#4caf50',
  error: '#f44336',
  warning: '#ff9800',
  info: '#2196f3',
};

/**
 * Shared base styles for all billing and CRM components
 * Reduces duplication across CRMProviderDashboard, CRMAdminDashboard, clinicDetails, etc.
 */
export const useBillingStyles = makeStyles((theme) => ({
  // Container styles
  root: {
    backgroundColor: BRAND_COLORS.white,
    borderRadius: 8,
  },
  
  // Card and section styles
  statCard: {
    backgroundColor: BRAND_COLORS.white,
    borderRadius: 8,
    padding: theme.spacing(2),
    height: '100%',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  
  sectionCard: {
    backgroundColor: BRAND_COLORS.white,
    borderRadius: 8,
    marginTop: theme.spacing(2),
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  
  cardHeader: {
    backgroundColor: BRAND_COLORS.lightGray,
    borderBottom: `2px solid ${BRAND_COLORS.orange}`,
  },
  
  // Avatar/Icon styles
  statIcon: {
    height: 48,
    width: 48,
    backgroundColor: BRAND_COLORS.navy,
    color: BRAND_COLORS.white,
  },
  
  // Button styles
  orangeButton: {
    backgroundColor: BRAND_COLORS.orange,
    color: BRAND_COLORS.white,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#e69520',
    },
  },
  
  navyButton: {
    backgroundColor: BRAND_COLORS.navy,
    color: BRAND_COLORS.white,
    '&:hover': {
      backgroundColor: '#001a3a',
    },
  },
  
  // Table styles
  tableHeader: {
    backgroundColor: BRAND_COLORS.lightGray,
    fontWeight: 600,
    color: BRAND_COLORS.navy,
  },
  
  // Text and typography styles
  cardTitle: {
    color: BRAND_COLORS.navy,
    fontWeight: 600,
    fontSize: 18,
  },
  
  sectionHeader: {
    color: BRAND_COLORS.navy,
    fontWeight: 600,
    fontSize: 16,
  },
  
  sectionValue: {
    color: BRAND_COLORS.darkGray,
    fontWeight: 500,
    fontSize: 14,
  },
  
  responsiveText: {
    fontSize: 'clamp(0.7875rem, 1.26vw, 0.85rem)',
  },
  
  // Status/condition styles
  healthActive: {
    color: BRAND_COLORS.success,
    fontWeight: 600,
  },
  
  healthWarning: {
    color: BRAND_COLORS.warning,
    fontWeight: 600,
  },
  
  trendUp: {
    color: BRAND_COLORS.success,
    fontWeight: 600,
  },
  
  trendDown: {
    color: BRAND_COLORS.error,
    fontWeight: 600,
  },
  
  // Loading and container styles
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  
  filterBar: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: BRAND_COLORS.lightGray,
    borderRadius: 8,
  },
  
  chartCard: {
    padding: theme.spacing(2),
    backgroundColor: BRAND_COLORS.lightGray,
    borderRadius: 8,
  },
  
  engagementBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: BRAND_COLORS.lightBlue,
  },
  
  // Form input styles
  orangeFocused: {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: BRAND_COLORS.orange,
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: BRAND_COLORS.navy,
    },
  },
  
  textfieldColor: {
    '& .MuiInputBase-input': {
      color: BRAND_COLORS.navy,
      fontSize: 'clamp(0.7875rem, 1.26vw, 0.85rem)',
    },
    '& .MuiInputLabel-root': {
      fontSize: 'clamp(0.7875rem, 1.26vw, 0.85rem)',
    },
  },
  
  textfieldSpacing: {
    marginBottom: 8,
  },
}));

/**
 * Currency formatter utility
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: DKK)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'DKK') => {
  return new Intl.NumberFormat('da-DK', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

/**
 * Get color based on health status
 */
export const getHealthColor = (status) => {
  switch (status) {
    case 'active':
      return BRAND_COLORS.success;
    case 'inactive_30d':
      return BRAND_COLORS.warning;
    case 'inactive_60d':
      return BRAND_COLORS.error;
    case 'at_risk':
      return '#d32f2f';
    default:
      return BRAND_COLORS.textSecondary;
  }
};

/**
 * Get color based on priority level
 */
export const getPriorityColor = (priority) => {
  switch (priority) {
    case 'critical':
      return '#d32f2f';
    case 'high':
      return '#f57c00';
    case 'medium':
      return '#fbc02d';
    case 'low':
      return '#689f38';
    default:
      return BRAND_COLORS.textSecondary;
  }
};

/**
 * Shared API base URL from environment or localhost
 */
export const getBackendUrl = () => {
  return process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
};

/**
 * Standard authentication headers for API calls
 * Usage: { ...getAuthHeaders(token) }
 */
export const getAuthHeaders = (token) => {
  return {
    'Authorization': `Token ${token}`,
    'Content-Type': 'application/json',
  };
};
